import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RiEqualizerLine } from 'react-icons/ri';
import { useSearch } from '../../../../SearchContext';
import {
  FilterDropdownContainer,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownSearch,
  EmptyState,
} from './styles';
import { Campo, scoreCampo, tokenize } from './utils';

interface FilterDropdownProps {
  disabled?: boolean;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  disabled = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const menuIdRef = useRef(
    `filter-menu-${Math.random().toString(36).slice(2)}`,
  );

  const {
    listaDeFiltrosOrdenados,
    handleOpenFiltro,
    resetBusca,
    setOpenFields,
    setIsCardModified,
    selectedCard,
  } = useSearch();

  // Flatten + apenas campos habilitados
  const allCampos = useMemo(
    () => (listaDeFiltrosOrdenados || []).flat().filter((c) => !!c.flg_filtro),
    [listaDeFiltrosOrdenados],
  );

  const qTokens = useMemo(() => tokenize(query), [query]);
  const qConcat = useMemo(() => qTokens.join(''), [qTokens]);

  // Aplica escoragem + ordena. Query vazia: mantém ordem natural.
  const rankedCampos: any[] = useMemo(() => {
    if (!query.trim()) {
      return allCampos.map((c) => ({
        ...c,
        score: 0,
        highlightedName: c.des_campo ?? '',
        highlightedSinonimo: c.des_sinonimo
          ? String(c.des_sinonimo)
          : undefined,
      }));
    }

    return allCampos
      .map((c: any) => scoreCampo(c, query, qTokens, qConcat))
      .filter((c) => c.score > 0 || qTokens.length === 0)
      .sort((a, b) =>
        b.score === a.score
          ? String(a.des_campo).localeCompare(String(b.des_campo))
          : b.score - a.score,
      );
  }, [allCampos, query, qTokens, qConcat]);

  useEffect(() => {
    if (disabled) {
      setShowDropdown(false);
      setActiveIndex(-1);
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        showDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setActiveIndex(-1);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [disabled, showDropdown]);

  useEffect(() => {
    if (!showDropdown) return;

    const handleKey = (e: KeyboardEvent) => {
      const max = rankedCampos.length - 1;
      switch (e.key) {
        case 'Escape':
          setShowDropdown(false);
          setActiveIndex(-1);
          buttonRef.current?.focus();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) => (prev < max ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : max));
          break;
        case 'Home':
          e.preventDefault();
          setActiveIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setActiveIndex(max);
          break;
        case 'Enter':
          if (activeIndex >= 0 && activeIndex <= max) {
            e.preventDefault();
            const item = rankedCampos[activeIndex];
            handleCampoClick(item);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showDropdown, rankedCampos, activeIndex]);

  useEffect(() => {
    if (!showDropdown) return;
    if (activeIndex >= 0) itemRefs.current[activeIndex]?.focus();
  }, [activeIndex, showDropdown]);

  useEffect(() => {
    if (!showDropdown) return;

    requestAnimationFrame(() => {
      const el = searchInputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(0, el.value.length);
      }
    });
  }, [showDropdown]);

  const toggleDropdown = useCallback(() => {
    if (disabled) return;

    setShowDropdown((open) => {
      const next = !open;
      setActiveIndex(next ? 0 : -1);
      return next;
    });
  }, [disabled]);

  const handleOpenWithKeyboard = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;

      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!showDropdown) {
          setShowDropdown(true);
          setActiveIndex(0);
        }
      }
    },
    [disabled, showDropdown],
  );

  const handleCampoClick = useCallback(
    (dadosDoCampo: any) => {
      if (disabled) return;

      delete dadosDoCampo.highlightedName;
      delete dadosDoCampo.highlightedSinonimo;
      delete dadosDoCampo.score;

      handleOpenFiltro(dadosDoCampo);
      setShowDropdown(false);
      setOpenFields(false);
      resetBusca();
      if (selectedCard) setIsCardModified(true);
      buttonRef.current?.focus();
    },
    [
      handleOpenFiltro,
      resetBusca,
      selectedCard,
      setIsCardModified,
      setOpenFields,
      disabled,
    ],
  );

  const hasQuery = useMemo(() => query.trim().length > 0, [query]);
  const topScore = hasQuery && rankedCampos.length ? rankedCampos[0].score : 0;

  return (
    <FilterDropdownContainer ref={dropdownRef}>
      <DropdownButton
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        onKeyDown={handleOpenWithKeyboard}
        aria-haspopup="menu"
        aria-expanded={showDropdown}
        aria-controls={menuIdRef.current}
        isOpen={showDropdown}
        disabled={disabled}
      >
        Adicionar filtro
        <RiEqualizerLine />
      </DropdownButton>

      {showDropdown && (
        <Dropdown
          id={menuIdRef.current}
          role="menu"
          aria-labelledby={menuIdRef.current}
          tabIndex={-1}
        >
          <DropdownSearch>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar campo ou sinônimo..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Buscar campo"
              onMouseEnter={() => setActiveIndex(-1)}
            />
          </DropdownSearch>

          {rankedCampos.length === 0 ? (
            <EmptyState role="status" aria-live="polite">
              Nenhum campo encontrado
            </EmptyState>
          ) : (
            rankedCampos
              .filter((i) => i.des_campo !== 'Filtro')
              .map((campo, idx) => {
                const title = `${campo.des_campo}${
                  campo.des_sinonimo ? ` (${String(campo.des_sinonimo)})` : ''
                }`;

                const isDimmed = hasQuery && campo.score < topScore * 0.6;

                return (
                  <DropdownItem
                    key={campo.cod_campo}
                    role="menuitem"
                    tabIndex={-1}
                    data-has-query={hasQuery ? 'true' : 'false'}
                    aria-label={title}
                    data-active={idx === activeIndex ? 'true' : 'false'}
                    data-dim={isDimmed ? 'true' : 'false'}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => handleCampoClick(campo as Campo)}
                    title={title}
                  >
                    <span
                      className="campo-nome"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: campo.highlightedName,
                      }}
                    />
                    {campo.des_sinonimo && (
                      <span
                        className="campo-sinonimo"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html:
                            campo.highlightedSinonimo ??
                            String(campo.des_sinonimo),
                        }}
                      />
                    )}
                  </DropdownItem>
                );
              })
          )}
        </Dropdown>
      )}
    </FilterDropdownContainer>
  );
};
